import { ORPCError } from "@orpc/client";
import type { AnyContractRouter } from "@orpc/contract";
import { ResponseValidationPlugin } from "@orpc/contract/plugins";
import { OpenAPILink } from "@orpc/openapi-client/fetch";
import {
  ERROR_CATALOG,
  errorDataSchema,
  errorResponseSchema,
  isErrorCode,
} from "@typedoc/contracts/errors";
import { AppError, errorFromBody } from "./app-error";
import { validateApiBase, type RpcFetch } from "./rpc/transport";

/** What each call to a contract client says about where and as whom it is sent. */
export type ContractCallContext = {
  /** The regional API base, such as `https://au.api.example/api` or the default `/api`. */
  base: string;
  authorization: string;
  fetcher?: RpcFetch | undefined;
  /** Sees the raw answer, so a refusal of its body can still cite the request ID. */
  onResponse?: ((response: Response) => void) | undefined;
};

/**
 * One link for a contract's client. Every success is checked against the contract's output
 * schema; a catalog envelope becomes the contract's typed `ORPCError` (its `data` holds the
 * request ID and any field issues); anything else is left for `appErrorOf` to describe by status.
 */
export function contractLink(contract: AnyContractRouter) {
  return new OpenAPILink<ContractCallContext>(contract, {
    url: ({ context }) => absolute(validateApiBase(context.base)),
    headers: ({ context }) => ({
      Authorization: context.authorization,
      Accept: "application/json",
    }),
    fetch: async (request, init, { context }) => {
      const send = context.fetcher ?? globalThis.fetch.bind(globalThis);
      const response = await send(request, { ...init, credentials: "omit" });
      context.onResponse?.(response);
      return response;
    },
    customErrorResponseBodyDecoder: (body, response) => {
      const parsed = errorResponseSchema.safeParse(body);
      if (!parsed.success || ERROR_CATALOG[parsed.data.error.code].status !== response.status) {
        return null;
      }
      const { code, message, requestId, issues } = parsed.data.error;
      return new ORPCError(code, { status: response.status, message, data: { requestId, issues } });
    },
    plugins: [new ResponseValidationPlugin(contract)],
  });
}

/**
 * The UI's error for a failed contract call: catalog refusals keep their code, request ID and
 * issues; an answer that is not the envelope is described by its status; a success the contract
 * does not describe is INVALID_RESPONSE. Transport failures and cancellation pass through for
 * `boundedRequest` to name.
 */
export function appErrorOf(error: unknown, answer: Response | undefined): unknown {
  if (error instanceof AppError) return error;
  if (error instanceof ORPCError) {
    const data = errorDataSchema.safeParse(error.data);
    if (isErrorCode(error.code) && data.success) {
      return new AppError(error.code, { status: error.status, ...data.data });
    }
    return errorFromBody(undefined, error.status, answer?.headers.get("X-Request-Id") ?? undefined);
  }
  if (!(error instanceof Error) || error instanceof TypeError) return error;
  if (error.name === "AbortError" || error.name === "TimeoutError") return error;
  return new AppError("INVALID_RESPONSE", {
    status: answer?.status,
    requestId: answer?.headers.get("X-Request-Id") ?? undefined,
  });
}

/**
 * A regional API base as an absolute address, for something outside this page that calls the same
 * API (the desktop app sending screens): a relative base such as the default `/api` is this page's.
 */
export function absoluteApiBase(base: string): string {
  return absolute(validateApiBase(base));
}

/** oRPC needs an absolute URL; a relative base such as the default `/api` is this page's. */
function absolute(base: string): string {
  if (!base.startsWith("/")) return base;
  const origin = globalThis.location?.origin;
  if (!origin) throw new Error("A relative API base needs a page origin.");
  return `${origin}${base === "/" ? "" : base}`;
}
